import { BackgroundJobStatus, BackgroundJobType } from 'types/graphql'

import { getBackgroundJobPillLabels } from 'src/fetch/backgroundJobs/backgroundJobPillLabels'
import { hasBackgroundJobPillNavigationContext } from 'src/fetch/backgroundJobs/backgroundJobPillNavigation'

import type { TrackedBackgroundJob } from './types'

/** Shape required from pending-jobs query rows before pill tracking. */
export type PendingBackgroundJobRowForTracking = {
  id: number
  type: BackgroundJobType
  status: BackgroundJobStatus
  isInTerminalStatus?: boolean | null
  viewId?: number | null
  viewName?: string | null
  recordTypeId?: number | null
  recordTypeName?: string | null
  processedCount: number
  totalCount: number
  successCount: number
  errorCount: number
}

/**
 * Maps a pending-jobs row to pill state. Returns null when type has no pill copy or list
 * view context are missing (e.g. bulk jobs started before the toolbar sends viewId).
 */
export const toTrackedBackgroundJob = (
  backgroundJob: PendingBackgroundJobRowForTracking
): TrackedBackgroundJob | null => {
  const pillLabels = getBackgroundJobPillLabels(backgroundJob.type)

  if (pillLabels == null || !hasBackgroundJobPillNavigationContext(backgroundJob)) {
    return null
  }

  const { viewId, recordTypeId } = backgroundJob

  return {
    id: backgroundJob.id,
    type: backgroundJob.type,
    status: backgroundJob.status,
    isInTerminalStatus: backgroundJob.isInTerminalStatus ?? false,
    pillActionLabel: pillLabels.pillActionLabel,
    pillTerminalActionLabel: pillLabels.pillTerminalActionLabel,
    processedCount: backgroundJob.processedCount,
    totalCount: backgroundJob.totalCount,
    successCount: backgroundJob.successCount,
    errorCount: backgroundJob.errorCount,
    viewId,
    viewName: backgroundJob.viewName ?? null,
    recordTypeId,
    recordTypeName: backgroundJob.recordTypeName ?? null,
  }
}

/**
 * Maps pending-jobs query rows to pill state. Row order matches the API response and drives
 * multi-job dropdown ordering.
 */
export const buildTrackedJobsFromPendingRows = (
  pendingBackgroundJobs: PendingBackgroundJobRowForTracking[]
): TrackedBackgroundJob[] => {
  return pendingBackgroundJobs.flatMap((backgroundJob) => {
    const trackedBackgroundJob = toTrackedBackgroundJob(backgroundJob)
    return trackedBackgroundJob ? [trackedBackgroundJob] : []
  })
}
